/**
 * Shared ORDER 26-S2 VWAP lifecycle.
 *
 * The store deliberately owns the poll loop, single-flight gate, failure back-off, and teardown
 * so two chart mounts observe one request stream. The fetcher is injected to keep this module
 * executable in a Node test without importing the browser-facing API client.
*/

import { reportDoorFailure, servedClosure } from "./faultEvidence.ts";

export interface VwapLevel {
  kind: "session" | "prior-day" | "naked-day" | "naked-week" | "naked-month";
  anchor_date: string;
  value: number;
  naked: boolean;
  set_at: string;
}

export interface VwapLevelsResponse {
  ok: boolean;
  root?: string;
  levels: VwapLevel[];
  count?: number;
  age_s?: number | null;
  budget_s?: number | null;
  stale?: boolean | null;
  vendor_ts?: string | null;
  receipt_ts?: string | null;
  poll_seq?: number | null;
  served_at?: string;
  study_status?: string;
  error?: string;
  reason?: string;
}

export interface VwapStoreSnapshot {
  feed: VwapLevelsResponse | null;
  error: string | null;
  failures: number;
}

export type VwapFetcher = () => Promise<VwapLevelsResponse>;
export interface VwapScheduler {
  setInterval(callback: () => void, delayMs: number): unknown;
  clearInterval(timer: unknown): void;
}
type VwapStoreListener = (snapshot: VwapStoreSnapshot) => void;
export interface ServedVenueVerdict { readonly verdict?: string | null }

export function createVwapStore(
  fetcher: VwapFetcher,
  scheduler: VwapScheduler = globalThis,
) {
  const listeners = new Set<VwapStoreListener>();
  let snapshot: VwapStoreSnapshot = { feed: null, error: null, failures: 0 };
  let timer: unknown = null;
  let inFlight = false;
  let vwapFailures = 0;
  let backoffStopped = false;
  let servedVenueState: ServedVenueVerdict | null = null;

  function notify(): void {
    for (const listener of listeners) listener(snapshot);
  }

  async function pollVwapStore(): Promise<void> {
    if (inFlight || vwapFailures >= 3) return;
    inFlight = true;
    try {
      const feed = await fetcher();
      if (feed.root !== undefined && feed.root !== "ES") throw new Error(`VWAP route returned root=${feed.root}`);
      vwapFailures = 0;
      backoffStopped = false;
      // Staleness is a data-age verdict owned by StalenessChip. It is not a transport failure:
      // putting it in `error` routes a valid stale payload through the ERROR branch and latched
      // the live page red after the weekend.
      snapshot = { feed, error: null, failures: 0 };
      notify();
    } catch (error) {
      vwapFailures += 1;
      const fault = reportDoorFailure("VWAP levels", error);
      snapshot = { feed: null, error: fault.label, failures: vwapFailures };
      if (vwapFailures >= 3 && timer !== null) {
        scheduler.clearInterval(timer);
        timer = null;
        backoffStopped = true;
      }
      notify();
    } finally {
      inFlight = false;
    }
  }

  function setServedVenueState(state: ServedVenueVerdict | null): void {
    servedVenueState = state;
    if (servedClosure(state)) {
      vwapFailures = 0;
      snapshot = { ...snapshot, failures: 0 };
      notify();
      return;
    }
    if (listeners.size > 0 && backoffStopped) {
      backoffStopped = false;
      ensureTimer();
      void pollVwapStore();
    }
  }

  function ensureTimer(): void {
    if (timer === null) timer = scheduler.setInterval(() => void pollVwapStore(), 60_000);
  }

  function startVwapPolling(listener: VwapStoreListener): () => void {
    listeners.add(listener);
    listener(snapshot);
    if (listeners.size === 1) {
      ensureTimer();
      void pollVwapStore();
    }
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0 && timer !== null) {
        scheduler.clearInterval(timer);
        timer = null;
      }
    };
  }

  function resetVwapPolling(): void {
    vwapFailures = 0;
    backoffStopped = false;
    snapshot = { ...snapshot, failures: 0, error: null };
    if (listeners.size > 0) {
      ensureTimer();
      notify();
      void pollVwapStore();
    }
  }

  return {
    startVwapPolling,
    subscribe: startVwapPolling,
    resetVwapPolling,
    setServedVenueState,
    pollVwapStore,
  };
}
